import React, { useEffect, useMemo, useRef, useState } from 'react';
import SegmentedControl from '../components/SegmentedControl.jsx';
import {
  fetchAdminCatalog,
  createJob as createCatalogJob,
  updateJob as updateCatalogJob,
  deleteJob as deleteCatalogJob,
  createCatalogTask,
  updateCatalogTask,
  deleteCatalogTask,
  updateMySettings,
  updateMyTheme,
  changeMyPassword,
  fetchManagerClients,
  archiveManagerClient,
  unarchiveManagerClient,
  deleteAdminClient,
  updateManagerClientColor,
  fetchManagerProjects,
  updateManagerProject,
  deleteManagerClient,
  deleteManagerProject,
  fetchPlanningLookups,
  fetchAdminUsers,
  createAdminUser,
  updateAdminUser,
  deleteAdminUser,
  fetchAuditLogs,
  createManagerClient,
} from '../api';
import toast from 'react-hot-toast';
import { deriveProjectColor, deriveProjectColorVariant } from '../utils/projectColors';
import { useAppRefresh } from '../context/AppRefreshContext';
import { isLocalOnlyThemeName } from '../ui/tokens';

export default function Settings({ user, theme, onThemeChange }) {
  const { bumpRefresh } = useAppRefresh();
  const [loading, setLoading] = useState(false);

  const canSeeRed2 = String(user?.email || '').trim().toLowerCase() === 'admin@example.com';

  const isAdmin = !!user && (String(user.role || '').toLowerCase() === 'admin' || String(user.display_name || '').trim().toLowerCase() === 'admin');
  const isManager = !!user && String(user.role || '').toLowerCase() === 'manager';
  const canManageClients = !!user && (isManager || isAdmin);
  const canManagePeople = !!user && (isManager || isAdmin);
  const canViewAudit = isAdmin || isManager;

  const [pwOpen, setPwOpen] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  // Job Catalog state
  const isCatalogAdmin = !!user && (isAdmin || isManager);
  const [catalog, setCatalog] = useState({ jobs: [] });
  const [showArchived, setShowArchived] = useState(false);
  const [newJobName, setNewJobName] = useState('');
  const [newTaskNameByJob, setNewTaskNameByJob] = useState({});

  const canViewPeopleDirectory = isAdmin || isManager;

  const [clientsLoading, setClientsLoading] = useState(false);
  const [clients, setClients] = useState([]); // [{id,name,color}]
  const [clientSavingById, setClientSavingById] = useState({});
  const [clientDeletingById, setClientDeletingById] = useState({});
  const [clientArchivingById, setClientArchivingById] = useState({});
  const [clientsShowArchived, setClientsShowArchived] = useState(false);

  const [newClientName, setNewClientName] = useState('');
  const [newClientColor, setNewClientColor] = useState('');
  const [newClientSaving, setNewClientSaving] = useState(false);

  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projects, setProjects] = useState([]); // [{id,name,client_name,client_id,client_color}]
  const [projectDeletingById, setProjectDeletingById] = useState({});
  const [projectsShowArchived, setProjectsShowArchived] = useState(false);
  const [projectArchivingById, setProjectArchivingById] = useState({});
  const [projectManagerOptions, setProjectManagerOptions] = useState([]);

  const [clientsSort, setClientsSort] = useState({ key: 'name', dir: 'asc' });
  const [projectsSort, setProjectsSort] = useState({ key: 'name', dir: 'asc' });

  const [projectEditId, setProjectEditId] = useState(null);
  const [projectEditForm, setProjectEditForm] = useState({ start_date: '', end_date: '', color_variant: 2, client_id: '', manager_user_id: '' });
  const [projectEditSaving, setProjectEditSaving] = useState(false);

  const clientSaveTimersRef = useRef({});

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmKind, setConfirmKind] = useState(null); // 'client' | 'project'
  const [confirmEntity, setConfirmEntity] = useState(null); // { id, name }
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmForceProject, setConfirmForceProject] = useState(false);
  const [confirmProjectNameInput, setConfirmProjectNameInput] = useState('');

  const [confirmClientAcknowledge, setConfirmClientAcknowledge] = useState(false);
  const [confirmClientDeleteText, setConfirmClientDeleteText] = useState('');

  const [confirmForceUser, setConfirmForceUser] = useState(false);
  const [confirmUserHasHistory, setConfirmUserHasHistory] = useState(false);
  const [confirmUserText, setConfirmUserText] = useState('');
  const [confirmNote, setConfirmNote] = useState('');

  const settingsTabs = useMemo(() => {
    const opts = [];
    if (canManageClients) opts.push({ key: 'clients', label: 'Clients' });
    if (canManageClients) opts.push({ key: 'projects', label: 'Projects' });
    if (isCatalogAdmin) opts.push({ key: 'jobs_tasks', label: 'Jobs & Tasks' });
    if (canViewPeopleDirectory) opts.push({ key: 'people', label: 'People' });
    if (canViewAudit) opts.push({ key: 'audit', label: 'Audit' });
    opts.push({ key: 'other', label: 'Other' });
    return opts;
  }, [canManageClients, isCatalogAdmin, canViewAudit]);

  const [tab, setTab] = useState(() => (canManageClients ? 'clients' : 'other'));
  useEffect(() => {
    // If role changes, keep current tab if still visible; otherwise reset.
    if (settingsTabs.some(t => t.key === tab)) return;
    setTab(canManageClients ? 'clients' : 'other');
  }, [settingsTabs, tab, canManageClients]);

  useEffect(() => {
    if (isCatalogAdmin) reloadCatalog();
  }, [isCatalogAdmin]);

  useEffect(() => {
    if (!canManageClients) return;
    let cancelled = false;
    (async () => {
      setClientsLoading(true);
      setProjectsLoading(true);
      try {
        const [cRes, pRes] = await Promise.all([
          fetchManagerClients({ includeArchived: clientsShowArchived }),
          fetchManagerProjects({ includeArchived: projectsShowArchived }),
        ]);
        if (!cancelled) {
          setClients(Array.isArray(cRes?.clients) ? cRes.clients : []);
          setProjects(Array.isArray(pRes?.projects) ? pRes.projects : []);
        }
      } catch (err) {
        if (!cancelled) {
          setClients([]);
          setProjects([]);
        }
        toast.error(err?.message || 'Failed to load clients/projects');
      } finally {
        if (!cancelled) {
          setClientsLoading(false);
          setProjectsLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [canManageClients, projectsShowArchived, clientsShowArchived]);

  useEffect(() => {
    if (!canManageClients) return;
    let cancelled = false;
    (async () => {
      try {
        const lookups = await fetchPlanningLookups({ redirectOn401: true });
        if (!cancelled) {
          setProjectManagerOptions(Array.isArray(lookups?.managers) ? lookups.managers : []);
        }
      } catch {
        if (!cancelled) setProjectManagerOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [canManageClients]);

  const reloadClients = async (includeArchived = clientsShowArchived) => {
    try {
      setClientsLoading(true);
      const cRes = await fetchManagerClients({ includeArchived });
      setClients(Array.isArray(cRes?.clients) ? cRes.clients : []);
    } catch (err) {
      toast.error(err?.message || 'Failed to load clients');
      setClients([]);
    } finally {
      setClientsLoading(false);
    }
  };

  // People directory
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [people, setPeople] = useState([]); // [{id,name,email,role,status}]
  const [peopleNote, setPeopleNote] = useState('');
  const [peopleShowDisabled, setPeopleShowDisabled] = useState(false);

  // Audit log
  const AUDIT_LIMIT = 50;
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState('');
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditOffset, setAuditOffset] = useState(0);
  const [auditExpanded, setAuditExpanded] = useState({}); // { [id]: true }
  const [auditFilters, setAuditFilters] = useState({
    since: '',
    until: '',
    action: '',
    actor: '',
    entity_type: '',
    entity_id: '',
  });

  const formatAuditMetadata = (metadata) => {
    if (!metadata) return '—';
    const s = String(metadata);
    try {
      const parsed = JSON.parse(s);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return s;
    }
  };

  const reloadAudit = async ({ offset = 0 } = {}) => {
    if (!canViewAudit) return;
    setAuditLoading(true);
    setAuditError('');
    try {
      const res = await fetchAuditLogs({
        ...auditFilters,
        limit: AUDIT_LIMIT,
        offset,
      });
      const logs = Array.isArray(res?.logs) ? res.logs : [];
      setAuditLogs(logs);
      setAuditTotal(Number(res?.total || 0));
      setAuditOffset(Number(res?.offset || offset || 0));
    } catch (err) {
      setAuditLogs([]);
      setAuditTotal(0);
      setAuditOffset(0);
      setAuditError(err?.message || 'Failed to load audit log');
    } finally {
      setAuditLoading(false);
    }
  };

  const managerOrder = useMemo(() => ['Vinko', 'Sanela', 'Andrea'], []);

  const formatDmyHm = (value) => {
    if (!value) return '—';
    const d = (value instanceof Date) ? value : new Date(value);
    if (isNaN(d.getTime())) return '—';
    const pad2 = (n) => String(n).padStart(2, '0');
    return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  };

  const formatDmy = (value) => {
    if (!value) return '—';
    const d = (value instanceof Date) ? value : new Date(value);
    if (isNaN(d.getTime())) return '—';
    const pad2 = (n) => String(n).padStart(2, '0');
    return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
  };

  const toYmdInput = (value) => {
    if (!value) return '';
    const d = (value instanceof Date) ? value : new Date(value);
    if (isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  };

  const openProjectEdit = (p) => {
    setProjectEditId(p?.id ?? null);
    setProjectEditSaving(false);
    setProjectEditForm({
      start_date: toYmdInput(p?.start_date),
      end_date: toYmdInput(p?.end_date),
      color_variant: (p?.color_variant != null) ? Number(p.color_variant) : 2,
      client_id: (p?.client_id != null) ? String(p.client_id) : '',
      manager_user_id: (p?.manager_user_id != null) ? String(p.manager_user_id) : '',
    });
  };

  const sortPeopleRows = (rows) => {
    const normalizeName = (v) => String(v || '').trim();
    const normalizeRole = (v) => String(v || '').toLowerCase();
    const orderIndex = (name) => {
      const n = normalizeName(name);
      const idx = managerOrder.findIndex(x => x.toLowerCase() === n.toLowerCase());
      return idx === -1 ? 999 : idx;
    };

    return (Array.isArray(rows) ? rows : []).slice().sort((a, b) => {
      const ar = normalizeRole(a.role);
      const br = normalizeRole(b.role);
      const aIsMgr = ar === 'manager' || ar === 'admin';
      const bIsMgr = br === 'manager' || br === 'admin';
      if (aIsMgr !== bIsMgr) return aIsMgr ? -1 : 1;

      if (aIsMgr && bIsMgr) {
        const ai = orderIndex(a.name);
        const bi = orderIndex(b.name);
        if (ai !== bi) return ai - bi;
        return normalizeName(a.name).localeCompare(normalizeName(b.name));
      }

      return normalizeName(a.name).localeCompare(normalizeName(b.name));
    });
  };

  const reloadPeople = async (includeDisabled = peopleShowDisabled) => {
    if (!canViewPeopleDirectory) {
      setPeople([]);
      setPeopleNote('People directory is available to managers/admins.');
      return;
    }
    setPeopleLoading(true);
    setPeopleNote('');
    try {
      try {
        const res = await fetchAdminUsers({ includeDisabled: !!includeDisabled });
        const rows = Array.isArray(res?.users) ? res.users : (Array.isArray(res) ? res : []);

        const shouldHideAdminRow = (u) => {
          if (isAdmin) return false;
          const email = String(u?.email || '').toLowerCase();
          const role = String(u?.role || '').toLowerCase();
          return email === 'admin@example.com' || role === 'admin';
        };

        const mapped = rows
          .filter(u => !shouldHideAdminRow(u))
          .map(u => ({
          id: u.id,
          name: u.display_name || u.name || u.username || '—',
          email: u.email || u.username || '—',
          role: u.role || 'employee',
          status: u.status || (u.active === false ? 'disabled' : 'active'),
          last_login_at: u.last_login_at || null,
          last_activity_at: u.last_activity_at || null,
        }));
        setPeople(sortPeopleRows(mapped));
        setPeopleLoading(false);
        return;
      } catch (err) {
        setPeopleNote('User directory endpoint not available yet; showing limited People list.');
      }

      const lookups = await fetchPlanningLookups({ redirectOn401: true });
      const managers = Array.isArray(lookups?.managers) ? lookups.managers : [];
      const employees = Array.isArray(lookups?.employees) ? lookups.employees : [];
      const rows = [
        ...managers.map(m => ({ id: m.id, name: m.display_name || '—', email: '—', role: 'manager', status: 'active' })),
        ...employees.map(e => ({ id: e.id, name: e.display_name || '—', email: '—', role: 'employee', status: 'active' })),
      ];
      setPeople(sortPeopleRows(rows));
    } catch (err) {
      setPeople([]);
      toast.error(err?.message || 'Failed to load people');
    } finally {
      setPeopleLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    if (!canViewPeopleDirectory) {
      setPeople([]);
      setPeopleNote('People directory is available to managers/admins.');
      return;
    }
    reloadPeople();
  }, [user?.role, canViewPeopleDirectory, peopleShowDisabled]);

  useEffect(() => {
    if (!user) return;
    if (tab !== 'audit') return;
    if (!canViewAudit) return;
    reloadAudit({ offset: 0 });
  }, [tab, user?.id, canViewAudit]);

  // Add/Edit person modal (manager/admin)
  const [personOpen, setPersonOpen] = useState(false);
  const [personMode, setPersonMode] = useState('create'); // create|edit
  const [personSaving, setPersonSaving] = useState(false);
  const [personErrors, setPersonErrors] = useState({});
  const [personForm, setPersonForm] = useState({
    id: null,
    name: '',
    email: '',
    role: 'employee',
    tempPassword: '',
    active: true,
  });

  function randomTempPassword() {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
    let out = '';
    for (let i = 0; i < 10; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
    return out;
  }

  function validatePerson(form) {
    const errs = {};
    const name = String(form.name || '').trim();
    const email = String(form.email || '').trim();
    const role = String(form.role || '').trim();
    const roleLower = String(role || '').toLowerCase();
    if (!name) errs.name = 'Name is required';
    if (!email) errs.email = 'Email is required';
    else if (!/^\S+@\S+\.\S+$/.test(email)) errs.email = 'Email looks invalid';
    if (isAdmin) {
      if (!['employee', 'junior_manager', 'manager', 'admin'].includes(roleLower)) errs.role = 'Role is required';
    } else if (isManager && personMode === 'edit') {
      // Managers can only flip employee <-> junior_manager.
      if (!['employee', 'junior_manager'].includes(roleLower)) errs.role = 'Managers can only set employee or junior_manager';
    } else {
      // Managers can only create employees.
      if (roleLower !== 'employee') errs.role = 'Role must be employee';
    }
    return errs;
  }

  // Removed legacy Clients/Departments management

  const reloadCatalog = async () => {
    try {
      const res = await fetchAdminCatalog();
      setCatalog(res || { jobs: [] });
    } catch (err) {
      toast.error('Failed to load catalog');
    }
  };

  const handleCreateJob = async () => {
    const name = newJobName.trim();
    if (!name) { toast.error('Job name required'); return; }
    try {
      await createCatalogJob({ name, catalog_group: 'core' });
      setNewJobName('');
      await reloadCatalog();
      toast.success('Job created');
    } catch (_) { toast.error('Failed to create job'); }
  };

  const handleUpdateJob = async (id, fields) => {
    try { await updateCatalogJob(id, fields); await reloadCatalog(); toast.success('Job updated'); }
    catch(_) { toast.error('Failed to update job'); }
  };

  const handleArchiveOrDeleteJob = async (id) => {
    try {
      const res = await deleteCatalogJob(id);
      await reloadCatalog();
      toast.success(res?.action === 'archived' ? 'Job archived' : 'Job deleted');
    } catch (_) { toast.error('Failed to archive/delete job'); }
  };

  const handleCreateTask = async (jobId) => {
    const name = String(newTaskNameByJob[jobId]||'').trim();
    if (!name) { toast.error('Task name required'); return; }
    try {
      await createCatalogTask(jobId, { name });
      setNewTaskNameByJob(prev => ({ ...prev, [jobId]: '' }));
      await reloadCatalog();
      toast.success('Task created');
    } catch (_) { toast.error('Failed to create task'); }
  };

  const handleUpdateTask = async (id, fields) => {
    try { await updateCatalogTask(id, fields); await reloadCatalog(); toast.success('Task updated'); }
    catch(_) { toast.error('Failed to update task'); }
  };

  const handleArchiveOrDeleteTask = async (id) => {
    try {
      const res = await deleteCatalogTask(id);
      await reloadCatalog();
      toast.success(res?.action === 'archived' ? 'Task archived' : 'Task deleted');
    } catch (_) { toast.error('Failed to archive/delete task'); }
  };

  const stableSort = (rows, compareFn) => {
    const list = Array.isArray(rows) ? rows : [];
    return list
      .map((row, idx) => ({ row, idx }))
      .sort((a, b) => {
        const c = compareFn(a.row, b.row);
        return c !== 0 ? c : (a.idx - b.idx);
      })
      .map(x => x.row);
  };

  const toggleSort = (setSort, key) => {
    setSort(prev => {
      if (String(prev?.key) === String(key)) {
        return { key, dir: prev?.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key, dir: 'asc' };
    });
  };

  const sortArrow = (sort, key) => {
    if (String(sort?.key) !== String(key)) return null;
    return sort?.dir === 'desc' ? '↓' : '↑';
  };

  const compareNullableNumber = (a, b) => {
    const aHas = a !== null && a !== undefined && Number.isFinite(a);
    const bHas = b !== null && b !== undefined && Number.isFinite(b);
    if (!aHas && !bHas) return 0;
    if (!aHas) return 1;
    if (!bHas) return -1;
    return a - b;
  };

  const compareString = (a, b) => {
    const as = String(a || '').toLowerCase();
    const bs = String(b || '').toLowerCase();
    return as.localeCompare(bs);
  };

  const projectsForList = useMemo(() => (Array.isArray(projects) ? projects.slice() : []), [projects]);

  const clientProjectCounts = useMemo(() => {
    const map = {};
    for (const p of (Array.isArray(projects) ? projects : [])) {
      const cid = p?.client_id != null ? String(p.client_id) : '';
      if (!cid) continue;
      map[cid] = (map[cid] || 0) + 1;
    }
    return map;
  }, [projects]);

  const sortedProjects = useMemo(() => {
    const dirFactor = projectsSort?.dir === 'desc' ? -1 : 1;
    const key = String(projectsSort?.key || 'name');
    return stableSort(projectsForList, (a, b) => {
      if (key === 'name') return dirFactor * compareString(a?.name, b?.name);
      if (key === 'client') return dirFactor * compareString(a?.client_name, b?.client_name);
      if (key === 'status') {
        const av = a?.archived ? 'archived' : 'active';
        const bv = b?.archived ? 'archived' : 'active';
        return dirFactor * compareString(av, bv);
      }
      if (key === 'created') {
        const at = a?.created_at || a?.createdAt;
        const bt = b?.created_at || b?.createdAt;
        const ams = at ? new Date(at).getTime() : null;
        const bms = bt ? new Date(bt).getTime() : null;
        return dirFactor * compareNullableNumber(ams, bms);
      }
      return dirFactor * compareString(a?.name, b?.name);
    });
  }, [projectsForList, projectsSort]);

  const sortedClients = useMemo(() => {
    const dirFactor = clientsSort?.dir === 'desc' ? -1 : 1;
    const key = String(clientsSort?.key || 'name');
    const list = Array.isArray(clients) ? clients.slice() : [];
    return stableSort(list, (a, b) => {
      if (key === 'name') return dirFactor * compareString(a?.name, b?.name);
      if (key === 'projectsCount') {
        const ac = clientProjectCounts[String(a?.id)] ?? null;
        const bc = clientProjectCounts[String(b?.id)] ?? null;
        return dirFactor * compareNullableNumber(Number.isFinite(ac) ? ac : null, Number.isFinite(bc) ? bc : null);
      }
      if (key === 'created') {
        const at = a?.created_at || a?.createdAt;
        const bt = b?.created_at || b?.createdAt;
        const ams = at ? new Date(at).getTime() : null;
        const bms = bt ? new Date(bt).getTime() : null;
        return dirFactor * compareNullableNumber(ams, bms);
      }
      return dirFactor * compareString(a?.name, b?.name);
    });
  }, [clients, clientsSort, clientProjectCounts]);

  const reloadProjects = async (includeArchived = projectsShowArchived) => {
    try {
      setProjectsLoading(true);
      const pRes = await fetchManagerProjects({ includeArchived });
      setProjects(Array.isArray(pRes?.projects) ? pRes.projects : []);
    } catch (err) {
      toast.error(err?.message || 'Failed to load projects');
      setProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  };

  const saveClientColor = async (clientId, color) => {
    try {
      setClientSavingById(prev => ({ ...prev, [clientId]: true }));
      const updated = await updateManagerClientColor(clientId, color);
      setClients(prev => prev.map(x => x.id === clientId ? { ...x, color: updated?.color || color } : x));

      // Refresh project list (client_color join) and bump global repaint/refetch
      try {
        const pRes = await fetchManagerProjects();
        setProjects(Array.isArray(pRes?.projects) ? pRes.projects : []);
      } catch {}

      try { bumpRefresh && bumpRefresh(); } catch {}
      toast.success('Client color updated');
    } catch (err) {
      toast.error(err?.message || 'Failed to update client color');
      throw err;
    } finally {
      setClientSavingById(prev => ({ ...prev, [clientId]: false }));
    }
  };

  return (
    <div className="w-full">
      <h2 className="text-xl font-bold mb-4">Settings</h2>

      <div className="mb-6">
        <SegmentedControl
          ariaLabel="Settings tabs"
          ariaRole="tablist"
          options={settingsTabs}
          value={tab}
          onChange={setTab}
        />
      </div>


      {tab === 'clients' && canManageClients && (
        <div className="mb-8 border rounded p-6 bg-white dark:bg-gray-800">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold">Clients</div>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 select-none">
              <input
                type="checkbox"
                checked={clientsShowArchived}
                onChange={async (e) => {
                  const next = !!e.target.checked;
                  setClientsShowArchived(next);
                  await reloadClients(next);
                }}
                disabled={clientsLoading}
              />
              Show archived
            </label>
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">Set a base color per client. Projects under that client use deterministic shades.</div>

          <div className="mb-4 flex flex-wrap items-end gap-3">
            <label className="flex flex-col">
              <span className="text-xs text-gray-600 dark:text-gray-400 mb-1">Client name</span>
              <input
                value={newClientName}
                onChange={(e) => setNewClientName(e.target.value)}
                className="border rounded px-2 py-2 bg-white dark:bg-gray-900 text-sm"
                placeholder="e.g. ACME"
              />
            </label>

            <label className="flex flex-col">
              <span className="text-xs text-gray-600 dark:text-gray-400 mb-1">Color (optional)</span>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={newClientColor || '#888888'}
                  onChange={(e) => setNewClientColor(e.target.value)}
                  className="h-10 w-12 p-0 border-0 bg-transparent"
                  title="Pick color"
                />
                <input
                  value={newClientColor}
                  onChange={(e) => setNewClientColor(e.target.value)}
                  className="border rounded px-2 py-2 bg-white dark:bg-gray-900 text-sm w-[120px]"
                  placeholder="#RRGGBB"
                />
              </div>
            </label>

            <button
              type="button"
              className="px-3 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700 text-sm disabled:opacity-60"
              disabled={newClientSaving}
              onClick={async () => {
                try {
                  const name = String(newClientName || '').trim();
                  if (name.length < 2 || name.length > 60) {
                    toast.error('Client name must be 2–60 characters');
                    return;
                  }
                  const color = String(newClientColor || '').trim();

                  setNewClientSaving(true);
                  const created = await createManagerClient({
                    name,
                    ...(color ? { color } : {}),
                  });

                  const row = {
                    id: created?.id,
                    name: created?.name || name,
                    color: created?.color || (color || '#888888'),
                    archived: false,
                    archived_at: null,
                    archived_by_user_id: null,
                  };
                  setClients(prev => {
                    const next = [...(Array.isArray(prev) ? prev : [])];
                    // avoid duplicates if API returns existing id/name
                    const exists = next.some(c => String(c.id) === String(row.id) || String(c.name || '').toLowerCase() === String(row.name || '').toLowerCase());
                    if (!exists) next.push(row);
                    next.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
                    return next;
                  });

                  setNewClientName('');
                  setNewClientColor('');

                  // Refresh projects list (client_name/color joins) and bump global refetch.
                  try {
                    const pRes = await fetchManagerProjects();
                    setProjects(Array.isArray(pRes?.projects) ? pRes.projects : []);
                  } catch {}

                  try { bumpRefresh && bumpRefresh(); } catch {}
                  toast.success('Client added');
                } catch (err) {
                  toast.error(err?.message || 'Failed to add client');
                } finally {
                  setNewClientSaving(false);
                }
              }}
            >
              {newClientSaving ? 'Adding…' : 'Add client'}
            </button>
          </div>

          {clientsLoading && <div className="text-sm text-gray-600 dark:text-gray-400">Loading clients…</div>}
          {!clientsLoading && clients.length === 0 && (
            <div className="text-sm text-gray-600 dark:text-gray-400">No clients found.</div>
          )}

          {!clientsLoading && clients.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-[900px] w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th
                      className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 cursor-pointer select-none"
                      onClick={() => toggleSort(setClientsSort, 'name')}
                      title="Sort"
                    >
                      Client Name <span className="text-[10px]">{sortArrow(clientsSort, 'name')}</span>
                    </th>
                    <th
                      className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 cursor-pointer select-none whitespace-nowrap"
                      onClick={() => toggleSort(setClientsSort, 'projectsCount')}
                      title="Sort"
                    >
                      Projects Count <span className="text-[10px]">{sortArrow(clientsSort, 'projectsCount')}</span>
                    </th>
                    <th
                      className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 cursor-pointer select-none whitespace-nowrap"
                      onClick={() => toggleSort(setClientsSort, 'created')}
                      title="Sort"
                    >
                      Created <span className="text-[10px]">{sortArrow(clientsSort, 'created')}</span>
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedClients.map(c => {
                    const saving = !!clientSavingById[c.id];
                    const deleting = !!clientDeletingById[c.id];
                    const archiving = !!clientArchivingById[c.id];
                    const isArchived = !!c.archived;
                    const count = clientProjectCounts[String(c.id)];

                    return (
                      <tr key={c.id} className={`border-b border-gray-100 dark:border-gray-800 ${isArchived ? 'opacity-60' : ''}`}>
                        <td className="px-3 py-2">
                          <div className="text-sm font-medium truncate flex items-center gap-2">
                            <span className="truncate">{c.name}</span>
                            {isArchived && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded border border-gray-300 dark:border-gray-700 text-[10px] text-gray-700 dark:text-gray-300 bg-white/60 dark:bg-gray-900/40">
                                Archived
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">{c.color}</div>
                        </td>

                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {Number.isFinite(Number(count)) ? Number(count) : '—'}
                        </td>

                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {formatDmy(c.created_at || c.createdAt)}
                        </td>

                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600" style={{ background: c.color }} />
                            <input
                              type="color"
                              value={c.color}
                              onChange={(e) => {
                                const v = e.target.value;
                                setClients(prev => prev.map(x => x.id === c.id ? { ...x, color: v } : x));

                                // Debounced auto-save
                                try {
                                  const timers = clientSaveTimersRef.current;
                                  if (timers[c.id]) clearTimeout(timers[c.id]);
                                  timers[c.id] = setTimeout(() => {
                                    saveClientColor(c.id, v).catch(() => {});
                                  }, 600);
                                } catch {}
                              }}
                              className="h-8 w-10 p-0 border-0 bg-transparent"
                              title="Pick color"
                            />

                            <button
                              type="button"
                              className="px-3 py-1 rounded bg-gray-900 text-white hover:bg-gray-800 text-sm disabled:opacity-60"
                              disabled={saving || deleting || archiving}
                              onClick={async () => {
                                // Cancel any pending debounce and save immediately
                                try {
                                  const timers = clientSaveTimersRef.current;
                                  if (timers[c.id]) clearTimeout(timers[c.id]);
                                  timers[c.id] = null;
                                } catch {}

                                await saveClientColor(c.id, c.color);
                              }}
                            >
                              {saving ? 'Saving…' : 'Save'}
                            </button>

                            <button
                              type="button"
                              className={`px-3 py-1 rounded text-white text-sm disabled:opacity-60 ${isArchived ? 'bg-green-600 hover:bg-green-700' : 'bg-yellow-600 hover:bg-yellow-700'}`}
                              disabled={saving || deleting || archiving}
                              onClick={async () => {
                                try {
                                  setClientArchivingById(prev => ({ ...prev, [c.id]: true }));
                                  if (isArchived) await unarchiveManagerClient(c.id);
                                  else await archiveManagerClient(c.id);
                                  await reloadClients(clientsShowArchived);
                                  try { bumpRefresh && bumpRefresh(); } catch {}
                                  toast.success(isArchived ? 'Client unarchived' : 'Client archived');
                                } catch (err) {
                                  toast.error(err?.message || 'Failed to update client');
                                } finally {
                                  setClientArchivingById(prev => ({ ...prev, [c.id]: false }));
                                }
                              }}
                            >
                              {archiving ? 'Saving…' : (isArchived ? 'Unarchive' : 'Archive')}
                            </button>

                            {isAdmin && (
                              <button
                                type="button"
                                className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 text-sm disabled:opacity-60"
                                disabled={deleting || saving || archiving}
                                onClick={() => {
                                  setConfirmKind('client');
                                  setConfirmEntity({ id: c.id, name: c.name });
                                  setConfirmForceProject(false);
                                  setConfirmProjectNameInput('');
                                  setConfirmClientAcknowledge(false);
                                  setConfirmClientDeleteText('');
                                  setConfirmOpen(true);
                                }}
                              >
                                {deleting ? 'Deleting…' : 'Force delete'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'projects' && canManageClients && (
        <div className="mb-8 border rounded p-6 bg-white dark:bg-gray-800">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold">Projects</div>
            <button
              type="button"
              className="text-sm px-3 py-1 rounded border bg-white dark:bg-gray-900"
              onClick={async () => {
                const next = !projectsShowArchived;
                setProjectsShowArchived(next);
                // reload immediately so list updates without waiting for effect
                await reloadProjects(next);
              }}
              disabled={projectsLoading}
            >
              {projectsShowArchived ? 'Hide archived' : 'Show archived'}
            </button>
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">Preview derived shades per project (based on the client base color).</div>

          {projectsLoading && <div className="text-sm text-gray-600 dark:text-gray-400">Loading projects…</div>}
          {!projectsLoading && projectsForList.length === 0 && (
            <div className="text-sm text-gray-600 dark:text-gray-400">No projects found.</div>
          )}

          {!projectsLoading && projectsForList.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-[900px] w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th
                      className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 cursor-pointer select-none"
                      onClick={() => toggleSort(setProjectsSort, 'name')}
                      title="Sort"
                    >
                      Project Name <span className="text-[10px]">{sortArrow(projectsSort, 'name')}</span>
                    </th>
                    <th
                      className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 cursor-pointer select-none"
                      onClick={() => toggleSort(setProjectsSort, 'client')}
                      title="Sort"
                    >
                      Client <span className="text-[10px]">{sortArrow(projectsSort, 'client')}</span>
                    </th>
                    <th
                      className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 cursor-pointer select-none"
                      onClick={() => toggleSort(setProjectsSort, 'status')}
                      title="Sort"
                    >
                      Status <span className="text-[10px]">{sortArrow(projectsSort, 'status')}</span>
                    </th>
                    <th
                      className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap cursor-pointer select-none"
                      onClick={() => toggleSort(setProjectsSort, 'created')}
                      title="Sort"
                    >
                      Created <span className="text-[10px]">{sortArrow(projectsSort, 'created')}</span>
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedProjects.map(p => {
                    const deleting = !!projectDeletingById[p.id];
                    const archiving = !!projectArchivingById[p.id];
                    const isArchived = !!p.archived;
                    const isEditing = projectEditId != null && String(projectEditId) === String(p.id);

                    const selectedClient = isEditing
                      ? (clients || []).find(c => String(c?.id) === String(projectEditForm.client_id))
                      : null;
                    const baseClientColor = (isEditing && selectedClient?.color) ? selectedClient.color : p.client_color;

                    const shade = (isEditing ? Number(projectEditForm.color_variant) : p.color_variant) != null
                      ? deriveProjectColorVariant(baseClientColor, p.id, isEditing ? Number(projectEditForm.color_variant) : p.color_variant)
                      : deriveProjectColor(baseClientColor, p.id);
                    const managerDisplay = p.manager_display_name || p.manager_name || p.manager_email || 'No manager assigned';

                    const durationLabel = `${formatDmy(p.start_date)} – ${formatDmy(p.end_date)}`;
                    const variants = [0, 1, 2, 3, 4].map(i => deriveProjectColorVariant(baseClientColor, p.id, i));

                    return (
                      <React.Fragment key={p.id}>
                        <tr className={`border-b border-gray-100 dark:border-gray-800 ${isArchived ? 'opacity-70' : ''}`}>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="w-5 h-5 rounded border border-gray-300 dark:border-gray-600 shrink-0" style={{ background: shade }} title={shade} />
                              <div className="min-w-0">
                                <div className="text-sm font-medium truncate flex items-center gap-2">
                                  <span className="truncate">{p.name}</span>
                                  {isArchived && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded border border-gray-300 dark:border-gray-700 text-[10px] text-gray-700 dark:text-gray-300 bg-white/60 dark:bg-gray-900/40">
                                      Archived
                                    </span>
                                  )}
                                </div>
                                <div className="text-[11px] text-gray-600 dark:text-gray-400 truncate">{durationLabel}</div>
                                <div className="text-[11px] text-gray-600 dark:text-gray-400 truncate">Manager: {managerDisplay}</div>
                              </div>
                            </div>
                          </td>

                          <td className="px-3 py-2">
                            <div className="truncate">{(isEditing ? (selectedClient?.name || p.client_name) : p.client_name) || '—'}</div>
                          </td>

                          <td className="px-3 py-2">
                            {isArchived ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded border border-gray-300 dark:border-gray-700 text-[11px] text-gray-700 dark:text-gray-300 bg-white/60 dark:bg-gray-900/40">
                                Archived
                              </span>
                            ) : (
                              <span className="text-[11px] text-gray-700 dark:text-gray-300">Active</span>
                            )}
                          </td>

                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            {formatDmy(p.created_at || p.createdAt)}
                          </td>

                          <td className="px-3 py-2 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                className="px-3 py-1 rounded border bg-white dark:bg-gray-900 text-sm"
                                onClick={() => {
                                  if (isEditing) setProjectEditId(null);
                                  else openProjectEdit(p);
                                }}
                                disabled={projectEditSaving || deleting || archiving}
                              >
                                {isEditing ? 'Close' : 'Edit'}
                              </button>

                              <button
                                type="button"
                                className={`px-3 py-1 rounded text-white text-sm disabled:opacity-60 ${isArchived ? 'bg-green-600 hover:bg-green-700' : 'bg-yellow-600 hover:bg-yellow-700'}`}
                                disabled={archiving || deleting}
                                onClick={async () => {
                                  try {
                                    setProjectArchivingById(prev => ({ ...prev, [p.id]: true }));
                                    await updateManagerProject(p.id, { archived: isArchived ? 0 : 1 });
                                    await reloadProjects(projectsShowArchived);
                                    try { bumpRefresh && bumpRefresh(); } catch {}
                                    toast.success(isArchived ? 'Project unarchived' : 'Project archived');
                                  } catch (err) {
                                    toast.error(err?.message || 'Failed to update project');
                                  } finally {
                                    setProjectArchivingById(prev => ({ ...prev, [p.id]: false }));
                                  }
                                }}
                              >
                                {archiving ? 'Saving…' : (isArchived ? 'Unarchive' : 'Archive')}
                              </button>

                              {canManageClients && (
                                <button
                                  type="button"
                                  className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 text-sm disabled:opacity-60"
                                  disabled={deleting}
                                  onClick={() => {
                                    setConfirmKind('project');
                                    setConfirmEntity({ id: p.id, name: p.name });
                                    setConfirmForceProject(false);
                                    setConfirmProjectNameInput('');
                                    setConfirmOpen(true);
                                  }}
                                >
                                  {deleting ? 'Deleting…' : 'Delete'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>

                        {isEditing && (
                          <tr className="border-b border-gray-100 dark:border-gray-800">
                            <td colSpan={5} className="px-3 py-3">
                              <div className="rounded border border-gray-200 dark:border-gray-700 p-3 bg-white/60 dark:bg-gray-900/40">
                                <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
                                  <label className="flex flex-col">
                                    <span className="text-xs text-gray-600 dark:text-gray-400 mb-1">Client</span>
                                    <select
                                      value={projectEditForm.client_id}
                                      onChange={(e) => setProjectEditForm(prev => ({ ...prev, client_id: e.target.value }))}
                                      className="border rounded px-2 py-2 bg-white dark:bg-gray-900 text-sm"
                                    >
                                      <option value="">—</option>
                                      {(clients || [])
                                        .filter(c => !c?.archived)
                                        .map(c => (
                                          <option key={c.id} value={String(c.id)}>{c.name}</option>
                                        ))}
                                    </select>
                                  </label>

                                  <label className="flex flex-col">
                                    <span className="text-xs text-gray-600 dark:text-gray-400 mb-1">Manager</span>
                                    <select
                                      value={projectEditForm.manager_user_id}
                                      onChange={(e) => setProjectEditForm(prev => ({ ...prev, manager_user_id: e.target.value }))}
                                      className="border rounded px-2 py-2 bg-white dark:bg-gray-900 text-sm"
                                    >
                                      <option value="">No manager assigned</option>
                                      {projectManagerOptions.map(m => (
                                        <option key={m.id} value={String(m.id)}>{m.display_name || m.email || `#${m.id}`}</option>
                                      ))}
                                    </select>
                                  </label>

                                  <label className="flex flex-col">
                                    <span className="text-xs text-gray-600 dark:text-gray-400 mb-1">Start date</span>
                                    <input
                                      type="date"
                                      value={projectEditForm.start_date}
                                      onChange={(e) => setProjectEditForm(prev => ({ ...prev, start_date: e.target.value }))}
                                      className="border rounded px-2 py-2 bg-white dark:bg-gray-900 text-sm"
                                    />
                                  </label>

                                  <label className="flex flex-col">
                                    <span className="text-xs text-gray-600 dark:text-gray-400 mb-1">End date</span>
                                    <input
                                      type="date"
                                      value={projectEditForm.end_date}
                                      onChange={(e) => setProjectEditForm(prev => ({ ...prev, end_date: e.target.value }))}
                                      className="border rounded px-2 py-2 bg-white dark:bg-gray-900 text-sm"
                                    />
                                  </label>

                                  <div>
                                    <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Color (client variants)</div>
                                    <div className="flex items-center gap-2">
                                      {variants.map((hex, idx) => {
                                        const selected = Number(projectEditForm.color_variant) === idx;
                                        return (
                                          <button
                                            key={idx}
                                            type="button"
                                            className={`w-7 h-7 rounded border border-gray-300 dark:border-gray-600 ${selected ? 'ring-2 ring-gray-900 dark:ring-gray-100' : ''}`}
                                            style={{ background: hex }}
                                            title={hex}
                                            aria-label={`Select color variant ${idx + 1}`}
                                            onClick={() => setProjectEditForm(prev => ({ ...prev, color_variant: idx }))}
                                          />
                                        );
                                      })}
                                    </div>
                                  </div>
                                </div>

                                <div className="mt-3 flex items-center justify-end gap-2">
                                  <button
                                    type="button"
                                    className="px-3 py-1 rounded border bg-white dark:bg-gray-900 text-sm"
                                    onClick={() => setProjectEditId(null)}
                                    disabled={projectEditSaving}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    className="px-3 py-1 rounded bg-gray-900 text-white hover:bg-gray-800 text-sm disabled:opacity-60"
                                    disabled={projectEditSaving}
                                    onClick={async () => {
                                      try {
                                        setProjectEditSaving(true);
                                        await updateManagerProject(p.id, {
                                          client_id: projectEditForm.client_id ? Number(projectEditForm.client_id) : null,
                                          manager_user_id: projectEditForm.manager_user_id ? String(projectEditForm.manager_user_id) : null,
                                          start_date: projectEditForm.start_date || null,
                                          end_date: projectEditForm.end_date || null,
                                          color_variant: Number(projectEditForm.color_variant),
                                        });
                                        await reloadProjects(projectsShowArchived);
                                        try { bumpRefresh && bumpRefresh(); } catch {}
                                        toast.success('Project updated');
                                        setProjectEditId(null);
                                      } catch (err) {
                                        toast.error(err?.message || 'Failed to update project');
                                      } finally {
                                        setProjectEditSaving(false);
                                      }
                                    }}
                                  >
                                    {projectEditSaving ? 'Saving…' : 'Save'}
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'people' && (
        <div className="space-y-8">
          {/* People management */}
          <div className="border rounded p-4 bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold">People</div>
              {canManagePeople && (
                <button
                  type="button"
                  className="text-sm px-3 py-2 rounded bg-gray-900 text-white"
                  title="Add a person"
                  onClick={() => {
                    setPersonMode('create');
                    setPersonErrors({});
                    setPersonForm({ id: null, name: '', email: '', role: 'employee', tempPassword: randomTempPassword(), active: true });
                    setPersonOpen(true);
                  }}
                >
                  + Add person
                </button>
              )}
            </div>
            {!canViewPeopleDirectory && (
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">People directory is available to managers/admins.</div>
            )}
            {canViewPeopleDirectory && isManager && (
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">Managers can add/edit employees, but cannot edit other managers.</div>
            )}
            {!!peopleNote && (
              <div className="text-xs text-amber-700 dark:text-amber-300 mb-3">{peopleNote}</div>
            )}

            {canViewPeopleDirectory && (
              <>
                <div className="flex items-center justify-between mb-2 gap-3">
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 select-none">
                    <input
                      type="checkbox"
                      checked={peopleShowDisabled}
                      onChange={async (e) => {
                        const next = !!e.target.checked;
                        setPeopleShowDisabled(next);
                        await reloadPeople(next);
                      }}
                    />
                    Show disabled
                  </label>
                  <button type="button" className="text-sm px-3 py-1 rounded border bg-white dark:bg-gray-900" onClick={() => reloadPeople(peopleShowDisabled)} disabled={peopleLoading}>Refresh</button>
                </div>

                {peopleLoading && <div className="text-sm text-gray-600 dark:text-gray-400">Loading people…</div>}
                {!peopleLoading && people.length === 0 && (
                  <div className="text-sm text-gray-600 dark:text-gray-400">No people found.</div>
                )}

                {!peopleLoading && people.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="min-w-[720px] w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-600 dark:text-gray-400">
                          <th className="py-2 pr-3">Name</th>
                          <th className="py-2 pr-3">Email</th>
                          <th className="py-2 pr-3">Role</th>
                          <th className="py-2 pr-3">Status</th>
                          <th className="py-2 pr-3">Last signed in</th>
                          <th className="py-2 pr-3">Last activity</th>
                          <th className="py-2 pr-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {people.map(p => (
                          <tr key={p.id} className="border-t border-gray-200 dark:border-gray-700">
                            <td className={`py-2 pr-3 whitespace-nowrap ${String(p.status||'').toLowerCase()==='disabled' ? 'text-gray-500 dark:text-gray-400 italic' : ''}`}>
                              {String(p.status||'').toLowerCase()==='disabled'
                                ? '(deactivated)'
                                : (String(p.name||'').trim().toLowerCase()==='deleted user' ? '—' : p.name)}
                            </td>
                            <td className="py-2 pr-3 whitespace-nowrap">{p.email}</td>
                            <td className="py-2 pr-3 whitespace-nowrap">{p.role}</td>
                            <td className="py-2 pr-3 whitespace-nowrap">{p.status}</td>
                            <td className="py-2 pr-3 whitespace-nowrap">{formatDmyHm(p.last_login_at)}</td>
                            <td className="py-2 pr-3 whitespace-nowrap">{formatDmyHm(p.last_activity_at)}</td>
                            <td className="py-2 pr-3 whitespace-nowrap">
                              {(() => {
                                const targetRole = String(p.role || '').toLowerCase();
                                const canEdit = isAdmin || (isManager && (targetRole === 'employee' || targetRole === 'junior_manager'));
                                const disabledReason = isManager && !(targetRole === 'employee' || targetRole === 'junior_manager')
                                  ? 'Managers cannot edit managers/admins'
                                  : '';
                                const canDelete = isAdmin || (isManager && targetRole === 'employee');
                                return (
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      className="text-sm px-2 py-1 rounded border bg-white dark:bg-gray-900 disabled:opacity-60"
                                      disabled={!canEdit}
                                      title={canEdit ? 'Edit person' : (disabledReason || 'Forbidden')}
                                      onClick={() => {
                                        if (!canEdit) return;
                                        setPersonMode('edit');
                                        setPersonErrors({});
                                        setPersonForm({
                                          id: p.id,
                                          name: p.name || '',
                                          email: p.email === '—' ? '' : (p.email || ''),
                                          role: targetRole === 'manager' ? 'manager' : (targetRole === 'junior_manager' ? 'junior_manager' : 'employee'),
                                          tempPassword: '',
                                          active: String(p.status || '').toLowerCase() !== 'disabled',
                                        });
                                        setPersonOpen(true);
                                      }}
                                    >
                                      Edit
                                    </button>

                                    <button
                                      type="button"
                                      className="text-sm px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                                      disabled={!canDelete}
                                      title={canDelete ? 'Delete user' : (disabledReason || 'Forbidden')}
                                      onClick={() => {
                                        if (!canDelete) return;
                                        setConfirmKind('person');
                                        setConfirmEntity({ id: p.id, name: p.name, role: targetRole });
                                        setConfirmForceProject(false);
                                        setConfirmProjectNameInput('');
                                        setConfirmForceUser(false);
                                        setConfirmUserHasHistory(false);
                                        setConfirmUserText('');
                                        setConfirmNote('');
                                        setConfirmOpen(true);
                                      }}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                );
                              })()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {tab === 'jobs_tasks' && isCatalogAdmin && (
        <div className="mb-8 border rounded p-4 bg-white dark:bg-gray-800">
          <h3 className="text-lg font-bold mb-2">Jobs & Tasks</h3>
          <div className="flex items-center space-x-2 mb-4">
            <input
              type="text"
              value={newJobName}
              onChange={e => setNewJobName(e.target.value)}
              placeholder="New job name"
              className="border p-2 rounded text-black"
            />
            <button onClick={handleCreateJob} className="bg-blue-500 text-white px-3 py-2 rounded">Add Job</button>
            <button onClick={()=>setShowArchived(!showArchived)} className="bg-gray-500 text-white px-3 py-2 rounded">
              {showArchived ? 'Hide Archived' : 'Show Archived'}
            </button>
          </div>

          <div className="space-y-4">
            {(catalog.jobs || []).filter(j=>!j.archived).map(job => (
              <div key={job.id} className="border rounded p-3">
                <div className="flex items-center justify-between">
                  <div className="font-semibold">{job.name}</div>
                  <div className="space-x-2">
                    <button onClick={()=>handleUpdateJob(job.id, { archived: true })} className="bg-yellow-600 text-white px-2 py-1 rounded">Archive</button>
                    <button onClick={()=>handleArchiveOrDeleteJob(job.id)} className="bg-red-600 text-white px-2 py-1 rounded">Delete</button>
                  </div>
                </div>
                <div className="mt-2 flex items-center space-x-2">
                  <input className="border p-1 rounded text-black" defaultValue={job.name} onBlur={e=>handleUpdateJob(job.id, { name: e.target.value })} />
                </div>
                <div className="mt-3 pl-4">
                  <div className="font-medium mb-2">Tasks</div>
                  <div className="space-y-2">
                    {(job.tasks||[]).filter(t=>!t.archived).map(t => (
                      <div key={t.id} className="flex items-center justify-between">
                        <input className="border p-1 rounded text-black mr-2" defaultValue={t.name} onBlur={e=>handleUpdateTask(t.id, { name: e.target.value })} />
                        <div className="space-x-2">
                          <button onClick={()=>handleUpdateTask(t.id, { archived: true })} className="bg-yellow-600 text-white px-2 py-1 rounded">Archive</button>
                          <button onClick={()=>handleArchiveOrDeleteTask(t.id)} className="bg-red-600 text-white px-2 py-1 rounded">Delete</button>
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center space-x-2 mt-2">
                      <input className="border p-1 rounded text-black" placeholder="New task name" value={newTaskNameByJob[job.id]||''} onChange={e=>setNewTaskNameByJob(prev=>({...prev,[job.id]:e.target.value}))} />
                      <button onClick={()=>handleCreateTask(job.id)} className="bg-blue-500 text-white px-2 py-1 rounded">Add Task</button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {showArchived && (
            <div className="mt-6">
              <div className="font-semibold mb-2">Archived</div>
              {(catalog.jobs||[]).filter(j=>j.archived).map(job => (
                <div key={job.id} className="border rounded p-3 opacity-70">
                  <div className="flex items-center justify-between">
                    <div>{job.name}</div>
                    <div className="space-x-2">
                      <button onClick={()=>handleUpdateJob(job.id, { archived: false })} className="bg-green-600 text-white px-2 py-1 rounded">Unarchive</button>
                    </div>
                  </div>
                  <div className="mt-2 pl-4">
                    {(job.tasks||[]).filter(t=>t.archived).map(t => (
                      <div key={t.id} className="flex items-center justify-between">
                        <div className="text-sm">{t.name}</div>
                        <button onClick={()=>handleUpdateTask(t.id, { archived: false })} className="bg-green-600 text-white px-2 py-1 rounded">Unarchive</button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'audit' && canViewAudit && (
        <div className="space-y-8">
          <div className="border rounded p-4 bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold">Audit</div>
              <button
                type="button"
                className="text-sm px-3 py-1 rounded border bg-white dark:bg-gray-900"
                onClick={() => reloadAudit({ offset: auditOffset || 0 })}
                disabled={auditLoading}
              >
                Refresh
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-xs mb-1 text-gray-700 dark:text-gray-300">Since (YYYY-MM-DD)</label>
                <input
                  type="date"
                  value={auditFilters.since}
                  onChange={(e) => setAuditFilters(prev => ({ ...prev, since: e.target.value }))}
                  className="w-full border rounded px-2 py-2 bg-white dark:bg-gray-900"
                />
              </div>
              <div>
                <label className="block text-xs mb-1 text-gray-700 dark:text-gray-300">Until (YYYY-MM-DD)</label>
                <input
                  type="date"
                  value={auditFilters.until}
                  onChange={(e) => setAuditFilters(prev => ({ ...prev, until: e.target.value }))}
                  className="w-full border rounded px-2 py-2 bg-white dark:bg-gray-900"
                />
              </div>
              <div>
                <label className="block text-xs mb-1 text-gray-700 dark:text-gray-300">Action</label>
                <input
                  type="text"
                  value={auditFilters.action}
                  onChange={(e) => setAuditFilters(prev => ({ ...prev, action: e.target.value }))}
                  placeholder="e.g. login_success"
                  className="w-full border rounded px-2 py-2 bg-white dark:bg-gray-900"
                />
              </div>
              <div>
                <label className="block text-xs mb-1 text-gray-700 dark:text-gray-300">Actor (email contains or user id)</label>
                <input
                  type="text"
                  value={auditFilters.actor}
                  onChange={(e) => setAuditFilters(prev => ({ ...prev, actor: e.target.value }))}
                  placeholder={isAdmin ? 'e.g. manager@example.com' : '—'}
                  className="w-full border rounded px-2 py-2 bg-white dark:bg-gray-900"
                  disabled={!isAdmin}
                  title={!isAdmin ? 'Managers only see their own audit log' : ''}
                />
              </div>
              <div>
                <label className="block text-xs mb-1 text-gray-700 dark:text-gray-300">Entity type</label>
                <input
                  type="text"
                  value={auditFilters.entity_type}
                  onChange={(e) => setAuditFilters(prev => ({ ...prev, entity_type: e.target.value }))}
                  placeholder="e.g. project"
                  className="w-full border rounded px-2 py-2 bg-white dark:bg-gray-900"
                />
              </div>
              <div>
                <label className="block text-xs mb-1 text-gray-700 dark:text-gray-300">Entity id</label>
                <input
                  type="text"
                  value={auditFilters.entity_id}
                  onChange={(e) => setAuditFilters(prev => ({ ...prev, entity_id: e.target.value }))}
                  placeholder="e.g. 123"
                  className="w-full border rounded px-2 py-2 bg-white dark:bg-gray-900"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 mb-3">
              <button
                type="button"
                className="text-sm px-3 py-2 rounded bg-gray-900 text-white disabled:opacity-60"
                disabled={auditLoading}
                onClick={() => reloadAudit({ offset: 0 })}
              >
                Apply
              </button>
              <button
                type="button"
                className="text-sm px-3 py-2 rounded border disabled:opacity-60"
                disabled={auditLoading}
                onClick={() => {
                  setAuditFilters({ since: '', until: '', action: '', actor: '', entity_type: '', entity_id: '' });
                  setAuditExpanded({});
                  setAuditOffset(0);
                  setAuditLogs([]);
                  setAuditTotal(0);
                }}
              >
                Clear
              </button>
            </div>

            {!!auditError && (
              <div className="text-sm text-red-600 dark:text-red-400 mb-2">{auditError}</div>
            )}

            <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-2">
              <div>Total: {auditTotal}</div>
              <div>Showing {auditOffset + 1}–{Math.min(auditOffset + AUDIT_LIMIT, auditTotal || 0)} (limit {AUDIT_LIMIT})</div>
            </div>

            <div className="flex items-center justify-between mb-3">
              <button
                type="button"
                className="text-sm px-3 py-1 rounded border bg-white dark:bg-gray-900 disabled:opacity-60"
                disabled={auditLoading || auditOffset <= 0}
                onClick={() => reloadAudit({ offset: Math.max(0, (auditOffset || 0) - AUDIT_LIMIT) })}
              >
                Newer
              </button>
              <button
                type="button"
                className="text-sm px-3 py-1 rounded border bg-white dark:bg-gray-900 disabled:opacity-60"
                disabled={auditLoading || (auditOffset + AUDIT_LIMIT) >= (auditTotal || 0)}
                onClick={() => reloadAudit({ offset: (auditOffset || 0) + AUDIT_LIMIT })}
              >
                Older
              </button>
            </div>

            {auditLoading && <div className="text-sm text-gray-600 dark:text-gray-400">Loading audit log…</div>}
            {!auditLoading && auditLogs.length === 0 && (
              <div className="text-sm text-gray-600 dark:text-gray-400">No audit events found.</div>
            )}

            {!auditLoading && auditLogs.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-[900px] w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-600 dark:text-gray-400">
                      <th className="py-2 pr-3">Time</th>
                      <th className="py-2 pr-3">Actor</th>
                      <th className="py-2 pr-3">Action</th>
                      <th className="py-2 pr-3">Entity</th>
                      <th className="py-2 pr-3">IP</th>
                      <th className="py-2 pr-3">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map(l => {
                      const expanded = !!auditExpanded[l.id];
                      const actorLabel = l.actor_email || (l.actor_user_id != null ? `User ${l.actor_user_id}` : '—');
                      const entityLabel = l.entity_label || (l.entity_id != null ? String(l.entity_id) : '—');
                      return (
                        <React.Fragment key={l.id}>
                          <tr className="border-t border-gray-200 dark:border-gray-700">
                            <td className="py-2 pr-3 whitespace-nowrap">{formatDmyHm(l.created_at)}</td>
                            <td className="py-2 pr-3 whitespace-nowrap">{actorLabel}</td>
                            <td className="py-2 pr-3 whitespace-nowrap">{l.action}</td>
                            <td className="py-2 pr-3 whitespace-nowrap">{l.entity_type} / {entityLabel}</td>
                            <td className="py-2 pr-3 whitespace-nowrap">{l.ip || '—'}</td>
                            <td className="py-2 pr-3 whitespace-nowrap">
                              <button
                                type="button"
                                className="text-sm px-2 py-1 rounded border bg-white dark:bg-gray-900"
                                onClick={() => setAuditExpanded(prev => ({ ...prev, [l.id]: !prev[l.id] }))}
                              >
                                {expanded ? 'Hide' : 'Show'}
                              </button>
                            </td>
                          </tr>
                          {expanded && (
                            <tr className="border-t border-gray-200 dark:border-gray-700">
                              <td colSpan={6} className="py-2">
                                <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">User-Agent: {l.user_agent || '—'}</div>
                                <pre className="text-xs whitespace-pre-wrap border rounded p-2 bg-gray-50 dark:bg-gray-900">
{formatAuditMetadata(l.metadata)}
                                </pre>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'other' && (
        <div className="space-y-8">
          <div className="border rounded p-4 bg-white dark:bg-gray-800">
            <div className="font-semibold mb-2">Theme</div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Choose your preferred theme. This setting persists with your account.</div>
            <div className="flex items-center gap-3">
              <select value={theme} onChange={async (e) => {
                const t = e.target.value;
                if (onThemeChange) await onThemeChange(t);
              }} className="border rounded px-2 py-1 bg-white dark:bg-gray-900">
                <option value="auroraExecutive">Dark</option>
                <option value="auroraExecutiveLight">Light</option>
                <option value="redLightPremium">Light Red</option>
                <option value="lightRedFocus">Red Prime</option>
                {canSeeRed2 && <option value="red2">Red 2</option>}
              </select>
            </div>
          </div>

          <div className="border rounded p-4 bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between">
              <div className="font-semibold">Password</div>
              <button
                type="button"
                className="text-sm px-3 py-1 rounded border bg-white dark:bg-gray-900"
                onClick={() => setPwOpen(v => !v)}
              >
                {pwOpen ? 'Close' : 'Change password'}
              </button>
            </div>

            {pwOpen && (
              <div className="mt-3 space-y-3">
                <div className="text-sm text-gray-600 dark:text-gray-400">Enter a new password and confirm it.</div>

                <div>
                  <label className="block text-xs mb-1 text-gray-700 dark:text-gray-300">New password</label>
                  <div className="flex items-center gap-2">
                    <input
                      type={showNewPw ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="flex-1 border rounded px-2 py-2 bg-white dark:bg-gray-900"
                      placeholder="New password"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="px-3 py-2 rounded border bg-white dark:bg-gray-900"
                      onClick={() => setShowNewPw(v => !v)}
                      title={showNewPw ? 'Hide password' : 'Show password'}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M2 12C4.5 7.5 8 5 12 5C16 5 19.5 7.5 22 12C19.5 16.5 16 19 12 19C8 19 4.5 16.5 2 12Z" stroke="currentColor" strokeWidth="2"/>
                        <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2"/>
                        {showNewPw && <path d="M4 20L20 4" stroke="currentColor" strokeWidth="2"/>}
                      </svg>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs mb-1 text-gray-700 dark:text-gray-300">Confirm new password</label>
                  <div className="flex items-center gap-2">
                    <input
                      type={showConfirmPw ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="flex-1 border rounded px-2 py-2 bg-white dark:bg-gray-900"
                      placeholder="Repeat new password"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="px-3 py-2 rounded border bg-white dark:bg-gray-900"
                      onClick={() => setShowConfirmPw(v => !v)}
                      title={showConfirmPw ? 'Hide password' : 'Show password'}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M2 12C4.5 7.5 8 5 12 5C16 5 19.5 7.5 22 12C19.5 16.5 16 19 12 19C8 19 4.5 16.5 2 12Z" stroke="currentColor" strokeWidth="2"/>
                        <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2"/>
                        {showConfirmPw && <path d="M4 20L20 4" stroke="currentColor" strokeWidth="2"/>}
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    className="text-sm px-3 py-2 rounded border"
                    onClick={() => { setNewPassword(''); setConfirmPassword(''); }}
                    disabled={pwSaving}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="text-sm px-3 py-2 rounded bg-indigo-600 text-white disabled:opacity-60"
                    disabled={pwSaving}
                    onClick={async () => {
                      try {
                        const np = String(newPassword || '');
                        const cp = String(confirmPassword || '');
                        if (!np || !cp) { toast.error('Enter new password and confirmation'); return; }
                        if (np !== cp) { toast.error('Passwords do not match'); return; }
                        setPwSaving(true);
                        await changeMyPassword(np, cp);
                        toast.success('Password changed');
                        setNewPassword('');
                        setConfirmPassword('');
                        setPwOpen(false);
                      } catch (err) {
                        toast.error(err?.message || 'Failed to change password');
                      } finally {
                        setPwSaving(false);
                      }
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add/Edit Person modal */}
      {personOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[6px] p-4">
          <div className="w-full max-w-lg rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold">{personMode === 'edit' ? 'Edit person' : 'Add person'}</div>
              <button type="button" className="text-sm px-2 py-1 rounded border" onClick={() => setPersonOpen(false)}>Close</button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs mb-1 text-gray-700 dark:text-gray-300">Name</label>
                <input
                  value={personForm.name}
                  onChange={(e) => setPersonForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border rounded px-2 py-2 bg-white dark:bg-gray-900"
                  placeholder="Full name"
                />
                {personErrors.name && <div className="text-xs text-red-600 mt-1">{personErrors.name}</div>}
              </div>

              <div>
                <label className="block text-xs mb-1 text-gray-700 dark:text-gray-300">Email</label>
                <input
                  value={personForm.email}
                  onChange={(e) => setPersonForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full border rounded px-2 py-2 bg-white dark:bg-gray-900"
                  placeholder="email@company.com"
                />
                {personErrors.email && <div className="text-xs text-red-600 mt-1">{personErrors.email}</div>}
              </div>

              <div>
                <label className="block text-xs mb-1 text-gray-700 dark:text-gray-300">Role</label>
                {isAdmin ? (
                  <>
                    <select
                      value={personForm.role}
                      onChange={(e) => setPersonForm(f => ({ ...f, role: e.target.value }))}
                      className="w-full border rounded px-2 py-2 bg-white dark:bg-gray-900"
                    >
                      <option value="admin">admin</option>
                      <option value="employee">employee</option>
                      <option value="junior_manager">junior_manager</option>
                      <option value="manager">manager</option>
                    </select>
                    {personErrors.role && <div className="text-xs text-red-600 mt-1">{personErrors.role}</div>}
                  </>
                ) : (isManager && personMode === 'edit') ? (
                  <>
                    <select
                      value={personForm.role}
                      onChange={(e) => setPersonForm(f => ({ ...f, role: e.target.value }))}
                      className="w-full border rounded px-2 py-2 bg-white dark:bg-gray-900"
                    >
                      <option value="employee">employee</option>
                      <option value="junior_manager">junior_manager</option>
                    </select>
                    {personErrors.role && <div className="text-xs text-red-600 mt-1">{personErrors.role}</div>}
                  </>
                ) : (
                  <div className="text-sm text-gray-600 dark:text-gray-400">employee</div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="block text-xs mb-1 text-gray-700 dark:text-gray-300">Temporary password</label>
                  <button
                    type="button"
                    className="text-xs px-2 py-1 rounded border bg-white dark:bg-gray-900"
                    onClick={() => setPersonForm(f => ({ ...f, tempPassword: randomTempPassword() }))}
                    disabled={!isAdmin && !isManager && personMode === 'edit'}
                    title={!isAdmin && !isManager && personMode === 'edit' ? 'Forbidden' : 'Generate'}
                  >
                    Generate
                  </button>
                </div>
                <input
                  value={personForm.tempPassword}
                  onChange={(e) => setPersonForm(f => ({ ...f, tempPassword: e.target.value }))}
                  className="w-full border rounded px-2 py-2 bg-white dark:bg-gray-900"
                  placeholder={personMode === 'edit' ? '(leave blank to keep unchanged)' : 'Temporary password'}
                  disabled={!isAdmin && !isManager && personMode === 'edit'}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="active"
                  type="checkbox"
                  checked={!!personForm.active}
                  onChange={(e) => setPersonForm(f => ({ ...f, active: e.target.checked }))}
                />
                <label htmlFor="active" className="text-sm text-gray-700 dark:text-gray-300">Active</label>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="text-sm px-3 py-2 rounded border"
                  onClick={() => setPersonOpen(false)}
                  disabled={personSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="text-sm px-3 py-2 rounded bg-gray-900 text-white disabled:opacity-60"
                  disabled={personSaving || !canManagePeople}
                  onClick={async () => {
                    const errs = validatePerson(personForm);
                    setPersonErrors(errs);
                    if (Object.keys(errs).length) return;
                    setPersonSaving(true);
                    try {
                      const canSetRole = isAdmin || (isManager && personMode === 'edit');
                      const canSetTempPassword = isAdmin || isManager || personMode !== 'edit';

                      const payload = {
                        display_name: String(personForm.name || '').trim(),
                        email: String(personForm.email || '').trim(),
                        role: canSetRole ? String(personForm.role || 'employee') : 'employee',
                        status: personForm.active ? 'active' : 'disabled',
                      };
                      const pw = String(personForm.tempPassword || '').trim();
                      if (pw && canSetTempPassword) payload.temp_password = pw;

                      if (personMode === 'edit' && personForm.id != null) {
                        await updateAdminUser(personForm.id, payload);
                        toast.success('User updated');
                      } else {
                        if (!payload.temp_password) payload.temp_password = randomTempPassword();
                        await createAdminUser(payload);
                        toast.success('User created');
                      }
                      setPersonOpen(false);
                      await reloadPeople();
                    } catch (err) {
                      toast.error(err?.message || 'Request failed');
                    } finally {
                      setPersonSaving(false);
                    }
                  }}
                >
                  {personSaving ? 'Saving…' : (personMode === 'edit' ? 'Save' : 'Create')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete modal */}
      {confirmOpen && confirmEntity && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[6px] p-4">
          <div className="w-full max-w-lg rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold">
                {confirmKind === 'client'
                  ? `Force delete client "${confirmEntity.name}"?`
                  : (confirmKind === 'project'
                    ? `Delete project "${confirmEntity.name}"?`
                    : `Delete user "${confirmEntity.name}"?`)}
              </div>
              <button type="button" className="text-sm px-2 py-1 rounded border" onClick={() => { if (!confirmBusy) setConfirmOpen(false); }}>
                Close
              </button>
            </div>

            <div className="text-sm text-gray-700 dark:text-gray-300">
              {confirmKind === 'client'
                ? 'This action cannot be undone. Force delete permanently deletes the client and related data (projects and time entries).' 
                : (confirmKind === 'project'
                  ? 'This action cannot be undone. If the project has work/planning, deletion is blocked unless you use force delete.'
                  : 'This action cannot be undone. If the user has history, deletion is blocked unless you use force delete (which deactivates + anonymizes).')}
            </div>

            {confirmKind === 'client' && isAdmin && (
              <div className="mt-3 border border-amber-200 dark:border-amber-900/50 rounded p-3 bg-amber-50/60 dark:bg-amber-900/10">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={confirmClientAcknowledge}
                    onChange={(e) => setConfirmClientAcknowledge(!!e.target.checked)}
                    disabled={confirmBusy}
                  />
                  <span className="font-medium">I understand this deletes related data</span>
                </label>
                <div className="mt-3">
                  <div className="text-xs text-gray-700 dark:text-gray-300 mb-1">Type DELETE to confirm:</div>
                  <input
                    value={confirmClientDeleteText}
                    onChange={(e) => setConfirmClientDeleteText(e.target.value)}
                    className="w-full border rounded px-2 py-2 bg-white dark:bg-gray-900"
                    placeholder="DELETE"
                    disabled={confirmBusy}
                  />
                </div>
              </div>
            )}

            {!!confirmNote && (
              <div className="mt-2 text-xs text-amber-700 dark:text-amber-300">{confirmNote}</div>
            )}

            {confirmKind === 'project' && isAdmin && (
              <div className="mt-3 border border-amber-200 dark:border-amber-900/50 rounded p-3 bg-amber-50/60 dark:bg-amber-900/10">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={confirmForceProject}
                    onChange={(e) => setConfirmForceProject(!!e.target.checked)}
                    disabled={confirmBusy}
                  />
                  <span className="font-medium">Force delete (deletes assignments and logged time)</span>
                </label>
                <div className="text-xs text-amber-800 dark:text-amber-200 mt-1">
                  Use only when the project cannot be deleted normally. This permanently deletes related work, including time entries.
                </div>

                {confirmForceProject && (
                  <div className="mt-3">
                    <div className="text-xs text-gray-700 dark:text-gray-300 mb-1">
                      Type the project name to confirm:
                    </div>
                    <input
                      value={confirmProjectNameInput}
                      onChange={(e) => setConfirmProjectNameInput(e.target.value)}
                      className="w-full border rounded px-2 py-2 bg-white dark:bg-gray-900"
                      placeholder={confirmEntity?.name || 'Project name'}
                      disabled={confirmBusy}
                    />
                  </div>
                )}
              </div>
            )}

            {confirmKind === 'person' && confirmUserHasHistory && (
              <div className="mt-3 border border-amber-200 dark:border-amber-900/50 rounded p-3 bg-amber-50/60 dark:bg-amber-900/10">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={confirmForceUser}
                    onChange={(e) => setConfirmForceUser(!!e.target.checked)}
                    disabled={confirmBusy}
                  />
                  <span className="font-medium">Force delete (deactivate + anonymize)</span>
                </label>
                <div className="text-xs text-amber-800 dark:text-amber-200 mt-1">
                  Force delete keeps history intact but removes PII (name/email) and disables the account.
                </div>

                {confirmForceUser && (
                  <div className="mt-3">
                    <div className="text-xs text-gray-700 dark:text-gray-300 mb-1">
                      Type the user name or the word DELETE to confirm:
                    </div>
                    <input
                      value={confirmUserText}
                      onChange={(e) => setConfirmUserText(e.target.value)}
                      className="w-full border rounded px-2 py-2 bg-white dark:bg-gray-900"
                      placeholder={(confirmEntity?.name || 'User name') + ' or DELETE'}
                      disabled={confirmBusy}
                    />
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-4">
              <button
                type="button"
                className="text-sm px-3 py-2 rounded border"
                disabled={confirmBusy}
                onClick={() => setConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="text-sm px-3 py-2 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                disabled={(() => {
                  if (confirmBusy) return true;
                  if (confirmKind === 'client') {
                    if (!isAdmin) return true;
                    if (!confirmClientAcknowledge) return true;
                    return String(confirmClientDeleteText || '').trim().toLowerCase() !== 'delete';
                  }
                  if (confirmKind === 'project') {
                    if (!isAdmin) return false;
                    if (!confirmForceProject) return false;
                    const want = String(confirmEntity?.name || '').trim().toLowerCase();
                    const got = String(confirmProjectNameInput || '').trim().toLowerCase();
                    return !want || want !== got;
                  }
                  if (confirmKind === 'person') {
                    if (!confirmUserHasHistory) return false;
                    if (!confirmForceUser) return false;
                    const want = String(confirmEntity?.name || '').trim().toLowerCase();
                    const got = String(confirmUserText || '').trim().toLowerCase();
                    return !(got && (got === want || got === 'delete'));
                  }
                  return false;
                })()}
                onClick={async () => {
                  if (!confirmEntity?.id) return;
                  setConfirmBusy(true);
                  try {
                    if (confirmKind === 'client') {
                      setClientDeletingById(prev => ({ ...prev, [confirmEntity.id]: true }));
                      await deleteAdminClient(confirmEntity.id, { force: true });
                      await reloadClients(clientsShowArchived);
                      toast.success('Client force deleted');
                    } else if (confirmKind === 'project') {
                      setProjectDeletingById(prev => ({ ...prev, [confirmEntity.id]: true }));
                      await deleteManagerProject(confirmEntity.id, { force: !!(isAdmin && confirmForceProject) });
                      setProjects(prev => prev.filter(x => x.id !== confirmEntity.id));
                      toast.success('Project deleted');
                    } else {
                      // person
                      try {
                        const res = await deleteAdminUser(confirmEntity.id, { force: !!confirmForceUser });
                        const status = String(res?.status || '').toLowerCase();
                        if (status === 'forced_deactivated') toast.success('User deactivated & anonymized');
                        else toast.success('User deleted');
                        await reloadPeople();
                        setConfirmOpen(false);
                        return;
                      } catch (err) {
                        // If server says history exists, reveal force option.
                        if (String(err?.message || '').toLowerCase().includes('history')) {
                          setConfirmUserHasHistory(true);
                          setConfirmNote('This user has history. You can force delete to deactivate + anonymize.');
                          toast.error('User has history (force delete required)');
                          return;
                        }
                        toast.error(err?.message || 'Delete failed');
                        return;
                      }
                    }
                    bumpRefresh();
                    setConfirmOpen(false);
                  } catch (err) {
                    toast.error(err?.message || 'Delete failed');
                  } finally {
                    if (confirmKind === 'client') {
                      setClientDeletingById(prev => ({ ...prev, [confirmEntity.id]: false }));
                    } else if (confirmKind === 'project') {
                      setProjectDeletingById(prev => ({ ...prev, [confirmEntity.id]: false }));
                    }
                    setConfirmBusy(false);
                  }
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
